import React from "react";
import { Link } from "react-router-dom";

import {
  formatDataRow,
  swapArrayElements,
  createDoughnutData,
  createEvolutionCharts,
} from "./performanceHelpers";

// Formateur pour la performance coût
export const formatCoutPerformance = (perf, type, isPrecisia, typeSearch) => {
  const titleColumns = isPrecisia
    ? ["Titre", "Statistique", "Objectif PrécisIA", "Atteinte"]
    : ["Titre", "Statistique", "Objectif", "Performance", "Atteinte"];

  const rows = [
    formatDataRow("Réparable (€HT)", perf.performance_cout.cout_reparable, isPrecisia),
  ];

  // Deuxième ligne : Gain ou VRADE selon le type
  if (type === 2 && perf.performance_cout.gain) {
    rows.push(formatDataRow("Gain (€HT)", perf.performance_cout.gain, isPrecisia));
  } else if (perf.performance_cout.cout_vrade) {
    rows.push(formatDataRow("VRADE (€HT)", perf.performance_cout.cout_vrade, isPrecisia));
  }

  return {
    data: {
      title: titleColumns,
      data: rows.filter(Boolean),
    },
    dataDoughnut: createDoughnutData(
      perf.performance_cout.value,
      perf.performance_cout.value_globale,
      typeSearch
    ),
    dataTab: createEvolutionCharts(
      { ...perf.performance_cout, type: "coût" },
      typeSearch,
      true
    ),
    dataTwo: null,
  };
};

// Formateur pour la performance délai
export const formatDelaiPerformance = (perf, type, typeSearch) => {
  let rows;

  if (type === 3) {
    // Assistante : nouvelle grille (délai CESSION → dépôt NR, mission/visite, stock > 120j)
    rows = [
      formatDataRow("Délai CESSION → dépôt NR (j)", perf.performance_delai.delai_cession_nr),
      formatDataRow("Délai mission/visite (j)", perf.performance_delai.delai_mission_visite),
      formatDataRow("Stock dossiers > 120 j (%)", perf.performance_delai.stock_120),
    ];
  } else {
    rows = [
      formatDataRow("Délai dépôt R (j)", perf.performance_delai.delai_depot_r),
    ];

    // Deuxième ligne selon le type (experts)
    if (type === 2) {
      rows.push(formatDataRow("Délai mission/EAD (j)", perf.performance_delai.delai_mission_ead));
    } else {
      rows.push(formatDataRow("Délai dépôt NR (j)", perf.performance_delai.delai_depot_nr));
      rows.push(formatDataRow("Délai mission-SD29 (j)", perf.performance_delai.delai_sd29));
      rows.push(formatDataRow("Délai visite-SD29 (j)", perf.performance_delai.delai_visite_sd29));
    }
  }

  return {
    data: {
      title: ["Titre", "Statistique", "Objectif", "Atteinte"],
      data: rows.filter(Boolean),
    },
    dataDoughnut: createDoughnutData(
      perf.performance_delai.value,
      perf.performance_delai.value_globale,
      typeSearch
    ),
    dataTab: createEvolutionCharts(
      { ...perf.performance_delai, type: "delai" },
      typeSearch
    ),
    dataTwo: null,
  };
};

// Formateur pour la performance production
export const formatProductionPerformance = (perf, typeSearch) => {
  const productivityRow = formatDataRow("Productivité (nb)", perf.performance_production.productivite);
  const swappedRow = productivityRow ? swapArrayElements(productivityRow, 1, 2) : null;

  let dataTwo = null;
  if (perf.displayHonorary === 1) {
    const honoraryRow = formatDataRow("Honoraire (€HT)", perf.performance_production.honoraire);
    const swappedHonoraryRow = honoraryRow ? swapArrayElements(honoraryRow, 1, 2) : null;

    dataTwo = {
      title: ["Titre", "A facturer", "Facturé", "Atteinte"],
      data: [swappedHonoraryRow].filter(Boolean),
    };
  }

  return {
    data: {
      title: ["Titre", "Reçu", "Déposé", "Atteinte"],
      data: [swappedRow].filter(Boolean),
    },
    dataDoughnut: createDoughnutData(
      perf.performance_production.value,
      perf.performance_production.value_globale,
      typeSearch
    ),
    dataTab: createEvolutionCharts(
      { ...perf.performance_production, type: "production" },
      typeSearch
    ),
    dataTwo,
  };
};

// Formateur pour la performance qualité
export const formatQualitePerformance = (perf, type, typeSearch) => {
  const rows = [
    formatDataRow("Nombre d'anomalies", perf.performance_qualite.nombre_anomalie, false, false, true),
  ];

  if (perf.performance_qualite.resultat_audit) {
    rows.push(formatDataRow("Résultat d'audit (%)", perf.performance_qualite.resultat_audit, false, false, false, "NC"));
  }

  // Résultat d'audit AP (pas pour type 3)
  if (type !== 3 && perf.performance_qualite.resultat_audit_AP) {
    rows.push(formatDataRow("Résultat d'audit AP (%)", perf.performance_qualite.resultat_audit_AP, false, false, false, 100));
  }

  if (perf.performance_qualite.precisiaPilotage) {
    rows.push(formatDataRow(<Link to="/quality/precisia-pilotage">Précisia Pilotage (%)</Link>, perf.performance_qualite.precisiaPilotage));
  }

  // Satisfaction
  if (perf.performance_qualite.hasOwnProperty("satisfaction")) {
    rows.push(formatDataRow("Satisfaction", perf.performance_qualite.satisfaction));
  }

  // Retard de gestion
  if (perf.performance_qualite.hasOwnProperty("delay")) {
    rows.push(formatDataRow(<Link to="/quality/delay">Retard de gestion (%)</Link>, perf.performance_qualite.delay, false, true));
  }

  return {
    data: {
      title: ["Titre", "Statistique", "Objectif", "Atteinte"],
      data: rows.filter(Boolean),
    },
    dataDoughnut: createDoughnutData(
      perf.performance_qualite.value,
      perf.performance_qualite.value_globale,
      typeSearch
    ),
    dataTab: createEvolutionCharts(
      { ...perf.performance_qualite, type: "qualite" },
      typeSearch
    ),
    dataTwo: null,
  };
};

// Formateur pour la performance globale
export const formatGlobalePerformance = (perf, typeSearch) => {
  return {
    data: null,
    dataDoughnut: createDoughnutData(
      perf.performance_globale.value,
      perf.performance_globale.value_globale,
      typeSearch
    ),
    dataTab: createEvolutionCharts(
      { ...perf.performance_globale, type: "" },
      typeSearch
    ).map((chart, index) => ({
      ...chart,
      label: index === 0 ? "Évolution performance" : "Évolution performance globale",
    })),
    dataTwo: null,
  };
};
